import './Main.css'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import DatePicker from 'react-datepicker'
import { useWizard } from '@/common/wizard/wizard'
import { useAppContext } from '@/context/app'
import { useModal } from '@/components/modal/Modal'
import { SuperAdminInsertMachineTypePopup } from '@/pages/for/superadmin/machinetype/MachineType'
import { SuperAdminInsertUserGroupPopup } from '@/pages/for/superadmin/usergroups/groups'
import { WizardData } from './Model'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { TimeDHM } from '@/components/Elements/time'
import { secondsToDHM } from '@/common/time'
import { Machine } from '@/services/model/machine'
import { MachineName, MachineNameData } from '@/components/machine'
import { useNavigate } from 'react-router-dom'
import { fetchPDF } from '@/common/fetch'

export interface MachineWorkingDays {
  machine: MachineNameData & { id: string };
  workingDays: string[] // ISO date strings (e.g. "2025-12-06")
}

export const MachineWorkingDaysReportWizardResult = () => {
  const { services, session, translates } = useAppContext()
  const { wizardData, updateWizardData, nextStep } = useWizard<WizardData>()
  const [data, setData] = useState<MachineWorkingDays[]>()
  const [pagginationState, setPagginationState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 30
  })

  function refresh() {
    services.session.fetchWithToken(`/api/communal/my/reports/machineworkingdays?from=${wizardData.from.toISOString()}&to=${wizardData.to.toISOString()}`).then((response) => {
      return response.json()
    }).then((data: MachineWorkingDays[]) => {
      setData(data)
    })
  }
    
  function loadPDF() {
    fetchPDF({
      url: `/api/communal/my/reports/machineworkingdays/pdf?from=${wizardData.from.toISOString()}&to=${wizardData.to.toISOString()}&lang=${session?.user.lang}`,
      fileName: "machineworkingdays.pdf",
      services
    })
  }

  useEffect(refresh, [])

  if (translates === undefined || wizardData === undefined || data === undefined) {
    return <div className='spinner'></div>
  }

  function headerRow(key: string, transKey?: string) {
    if (translates === undefined || wizardData === undefined) {
      return null
    }
    transKey = transKey ? transKey : key
    return <div className='hcol'>
      {translates.for('report.machineworkingdays.summary.table.' + transKey)}
    </div>
  }

  const renderTable = (
    data: MachineWorkingDays[],
    state: StaticPaginatedTableState,
    setPagginationState: React.Dispatch<React.SetStateAction<StaticPaginatedTableState>> | undefined
  ) => {
    return <StaticPaginatedTable<MachineWorkingDays>
      data={data}
      parentState={state}
      setParentState={setPagginationState}
      header={() => <>
        {headerRow('machinename', 'machinename')}
        {headerRow('workingdays', 'workingdays')}
      </>}
      row={(row) => {
        const openDetails = () => {
          const url = `${window.location.origin}/communal/statistics/machines?machine=${row.machine.id}`;
          window.open(url, '_blank');
        }
        return <>
          <div className={`col`} onClick={openDetails}>
            <MachineName row={row.machine} />
          </div>
          <div className={`col`}>
            <DateLinks machineId={row.machine.id} dates={row.workingDays} />
          </div>
        </>
      }}
      translates={translates}
    />
  }

  return <>
    <div>
      <h2>{translates.for('report.machineworkingdays.summary.title')}</h2>
      {renderTable(data, pagginationState, setPagginationState)}

      <div className='right-btns right-btns-margin'>
        <button type="button" className='btn btn-next' onClick={loadPDF}>{translates.for('report.usersranking.wizard.gen_pdf')}</button>
      </div>
    </div>
  </>
}

const DateLinks: React.FC<{
  machineId: string
  dates: string[]
}> = (props) => {
  const handleClick = (dateStr: string) => {
    const end = new Date(dateStr)
    end.setHours(23, 59, 59, 999)

    const url = `${window.location.origin}/communal/statistics/machines?length=1d&machine=${props.machineId}&for=${end.toISOString()}`
    window.open(url, '_blank')
  };

  return (
    <div>
      {props.dates.map((date, index) => (
        <React.Fragment key={date}>
          <span
            className='day-link'
            onClick={() => handleClick(date)}
          >
            {date}
          </span>
          {index < props.dates.length - 1 && ", "}
        </React.Fragment>
      ))}
    </div>
  )
}